import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, Output, forwardRef, inject } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import TmwAbbreviate from '../../../pipes/abbreviate.pipe';
// Removed HttpClient, HttpEventType
import { FileRepositoryService } from '@common/service/file-repository.service'; // Keep for openFile if `id` is still needed
import { DataUtils, FileLoadError } from '@core/utils/data-util.service';
import { TmwTranslateDirective } from '@shared/directives/translate.directive';
import { Subscription } from 'rxjs';


const noop = () => {
  return;
};

const MULTI_FILEUPLOAD_CONTROL_VALUE_ACCESSOR = {
  multi: true,
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TmwMultiFileUploadComponent),
};

@Component({
  selector: 'tmw-multi-file-upload',
  standalone: true,
  providers: [MULTI_FILEUPLOAD_CONTROL_VALUE_ACCESSOR],
  imports: [CommonModule, FormsModule, forwardRef(() => TmwTranslateDirective), TmwAbbreviate],
  templateUrl: './multi-file-upload.component.html',
  styleUrl: './multi-file-upload.component.scss',
})
export class TmwMultiFileUploadComponent {
  private readonly destroy: DestroyRef = inject(DestroyRef);
  // private httpClient = inject(HttpClient); // No longer needed for immediate upload
  fileRepositoryService = inject(FileRepositoryService); // Keep for openFile if `id` is still needed
  protected dataUtils: DataUtils = inject(DataUtils);


  @Input() accept: string = '*/*'; // Allowed file types
  @Input() maxFileSize: number = 5 * 1024 * 1024; // 5 MB
  // @Input() uploadUrl: string = '/api/file-repository'; // No longer relevant
  @Input("readOnly") readOnlyInput?: boolean = false;
  @Input() useBase64?: boolean = true;


  files: any[] = [];
  disabled: boolean = false;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  @Output() filesChange = new EventEmitter();

  constructor() {}

  // ControlValueAccessor methods
  writeValue(value: any[]): void {
    if (value) {
      this.files = value.map(item => ({
        ...item,
        status: item.status || 'done' // Default to 'done' if status is not provided
      }));
    } else {
      this.files = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFileSelected(event: Event): void {
    if (this.disabled || this.readOnlyInput) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (file.size > this.maxFileSize) {
          // Handle file size error
          this.files.push({
            fichierFile: file,
            fichierName: file.name,
            fichierMimeType: file.type,
            fichierContentType: file.type,
            status: 'error',
            errorMessage: `File size exceeds ${this.maxFileSize / (1024 * 1024)} MB`
          });
          continue;
        }
        this.addFile(file);
      }
      // Clear the input to allow selecting the same file again
      input.value = '';
    }
  }

  addFile(file: File) {
    const tempForm = {}; // Temporary object to pass to loadFileToForm
    const fieldName = 'fileRepository'; // A dummy field name for DataUtils

    const subscription: Subscription = this.dataUtils.loadFileToForm(
      { target: { files: [file] } } as unknown as Event, // Cast to Event
      tempForm,
      fieldName,
      false, // isImage, assuming false for generic files
      false, // isExcel, assuming false for generic files
      this.useBase64 // useBase64 based on component input
    ).subscribe({
      next: () => {
        const localFile: any = {
          fichierFile: file,
          fichierName: tempForm[`${fieldName}Name`],
          fichierMimeType: tempForm[`${fieldName}ContentType`],
          fichierContentType: tempForm[`${fieldName}ContentType`],
          status: 'done'
        };
        if (this.useBase64) {
          localFile.fichierFile = tempForm[`${fieldName}File`];
        }
        this.files.push(localFile);
        this.updateValue(); // Notify parent of change
      },
      error: (err: FileLoadError) => {
        this.files.push({
          fichierFile: file,
          fichierName: file.name,
          fichierMimeType: file.type,
          fichierContentType: file.type,
          status: 'error',
          errorMessage: err.message
        });
        this.updateValue(); // Notify parent of change
        console.error('Error loading file to form:', err);
      },
      complete: () => {
        this.onTouchedCallback();
      }
    });

    this.destroy.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  removeFile(fileToRemove: any): void {
    if (this.readOnlyInput) {
      return;
    }
    this.files = this.files.filter(file => file !== fileToRemove);
    this.updateValue(); // Notify parent of change
    this.onTouchedCallback();
  }

  openFile(localFile: any): void {
    if (localFile.fileRepositoryFile) {
      this.dataUtils.openFile(localFile.fileRepositoryFile, localFile.fileRepositoryMimeType, localFile.fileRepositoryFileName);
    } else if (localFile.originalFile && !this.useBase64) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          this.dataUtils.openFile(e.target.result.toString(), localFile.originalFile.type, localFile.originalFile.name);
        }
      };
      reader.readAsDataURL(localFile.originalFile);
    } else if (localFile.id) {
      // Fallback: If an ID exists, try to fetch from FileRepositoryService (for pre-existing files)
      this.fileRepositoryService.getFile(localFile.id).subscribe({
        next: (result) => {
        },
        error: (err) => {
          console.error('Error opening file from server:', err);
        }
      });
    }
  }


  private updateValue(): void {
    this.filesChange.emit(this.files)
    // Emit the array of LocalFile objects
    this.onChangeCallback(this.files);
  }
}
